import React from 'react';
import { Tag24Regular } from '@fluentui/react-icons';
import { type CustomerFormBag } from './types';
import { useSectionStyles } from './styles';

interface TagsSectionProps {
  form: CustomerFormBag;
  /** Hide the title icon when the section is hosted in a context that
   *  already provides framing (e.g. the profile page's card wrapper). */
  showIcon?: boolean;
}

/**
 * Tag chips with an inline input. Edit-mode only — Add mode hides
 * tags since there's no record to attach them to until creation.
 * Contacts inherit tags from their parent Company so this section
 * isn't shown for them either.
 */
const TagsSection: React.FC<TagsSectionProps> = ({ form, showIcon = true }) => {
  const styles = useSectionStyles();
  const {
    tags,
    newTag, setNewTag,
    handleAddTag, handleRemoveTag,
    triggerSave,
  } = form;

  return (
    <>
      <div className={styles.sectionTitle}>{showIcon && <Tag24Regular />}Tags</div>
      <div className={styles.formField}>
        <label className={styles.label}>Customer tags</label>
        <div
          className={styles.tagField}
          onClick={(e) => {
            // Clicking anywhere in the field that isn't a chip focuses
            // the input — feels like a single combined control.
            const target = e.target as HTMLElement;
            if (target === e.currentTarget) {
              (target.querySelector('input') as HTMLInputElement | null)?.focus();
            }
          }}
        >
          {tags.map((tag, index) => (
            <span key={index} className={styles.tagChip}>
              {tag}
              <span
                className={styles.tagDismiss}
                role="button"
                aria-label={`Remove ${tag}`}
                onClick={() => handleRemoveTag(tag)}
              >
                ×
              </span>
            </span>
          ))}
          <input
            className={styles.tagInlineInput}
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                handleAddTag();
              } else if (e.key === 'Backspace' && newTag === '' && tags.length > 0) {
                e.preventDefault();
                handleRemoveTag(tags[tags.length - 1]);
              }
            }}
            onBlur={() => {
              if (newTag.trim()) handleAddTag();
              triggerSave();
            }}
            placeholder={tags.length === 0 ? 'Add tags…' : ''}
          />
        </div>
      </div>
    </>
  );
};

export default TagsSection;
